import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
// Custom Stethoscope SVG
const StethoscopeIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-253">
    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" data-id="element-254" />
    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" data-id="element-255" />
    <circle cx="20" cy="10" r="2" data-id="element-256" />
  </svg>;
// Custom Bug SVG
const BugIcon = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-257">
    <rect width="8" height="14" x="8" y="6" rx="4" data-id="element-258" />
    <path d="m19 7-3 2" data-id="element-259" />
    <path d="m5 7 3 2" data-id="element-260" />
    <path d="m19 19-3-2" data-id="element-261" />
    <path d="m5 19 3-2" data-id="element-262" />
    <path d="M20 13h-4" data-id="element-263" />
    <path d="M4 13h4" data-id="element-264" />
    <path d="m9 4 1 2" data-id="element-265" />
    <path d="m15 4-1 2" data-id="element-266" />
  </svg>;
export function HeroSection() {
  return <section className="relative min-h-screen w-full flex items-center overflow-hidden bg-brand-black pt-20" data-id="element-267">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat md:bg-right" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/7i5rcztuhkmcTdMeFvfnFG/heylloyd-image-1773463882812.png')`,
      backgroundPosition: 'calc(100% + 10vw) center'
    }} data-id="element-268" />

      {/* Gradient Overlay to make text readable on the left */}
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-brand-black via-brand-black/90 to-transparent md:w-3/4" data-id="element-269" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-brand-black via-transparent to-transparent md:hidden" data-id="element-270" />

      {/* Cinematic Green Flare */}
      <div className="absolute left-0 top-1/4 w-96 h-96 bg-brand-green/20 rounded-full blur-[120px] z-10 pointer-events-none" data-id="element-271" />

      <div className="relative z-20 container mx-auto px-6 md:px-12 pt-20 pb-20 md:py-0" data-id="element-272">
        <div className="max-w-3xl" data-id="element-273">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-274">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-brand-darker border border-brand-green/30 px-4 py-1.5 rounded-full mb-8 shadow-[0_0_15px_rgba(16,185,129,0.15)]" data-id="element-275">
              <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" data-id="element-276" />
              <span className="text-xs font-bold tracking-widest text-brand-gray uppercase" data-id="element-277">
                Control de Plagas
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-brand-yellow leading-[1.1] mb-6 drop-shadow-lg" data-id="element-278">
              ¿Tu agenda está infestada de espacios vacíos?
              <br data-id="element-279" />
              <span className="text-white flex items-center flex-wrap gap-3 mt-2" data-id="element-280">
                Aplicamos la solución definitiva.
                <span className="flex items-center space-x-1 text-brand-yellow" data-id="element-281">
                  <StethoscopeIcon className="w-8 h-8 md:w-10 md:h-10" data-id="element-282" />
                  <BugIcon className="w-8 h-8 md:w-10 md:h-10" data-id="element-283" />
                </span>
              </span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg md:text-xl text-brand-gray mb-10 max-w-2xl leading-relaxed" data-id="element-284">
              Leads de alto ticket (Termitas y Roedores) directo a tu celular.
              Control de crecimiento por{' '}
              <span className="text-white font-semibold" data-id="element-285">Doctor Ecommerce</span>
              .
            </p>

            {/* CTAs */}
            <div className="flex flex-col items-start space-y-4" data-id="element-286">
              <motion.a href="#contact-form" whileHover={{
              scale: 1.05,
              boxShadow: '0 0 30px rgba(212, 168, 67, 0.4)'
            }} whileTap={{
              scale: 0.95
            }} className="inline-block bg-brand-yellow text-brand-black font-heading font-bold text-xl px-10 py-5 rounded-md uppercase tracking-wide transition-all duration-300 text-center w-full sm:w-auto" data-id="element-287">
                Solicitar Estimado de Leads
              </motion.a>

              <a href="https://calendar.app.google/gDsqAVqmwUYm" target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-gray hover:text-white transition-colors group text-sm md:text-base" data-id="element-288">
                <CalendarIcon className="w-4 h-4 mr-2 text-brand-yellow group-hover:scale-110 transition-transform" data-id="element-289" />
                <span className="underline decoration-brand-gray/50 underline-offset-4 group-hover:decoration-brand-yellow/50" data-id="element-290">
                  O agenda tu cita directamente →
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}